/**
 * useTierState — single React hook for reading the authoritative paid/free
 * state from `/api/payments/me` (the G1 backend computes the canonical
 * answer; this just surfaces it to UI).
 *
 * Cached at the hook level — first call fetches, subsequent calls share
 * the same response within the page session. Use across every paywall
 * surface (Library gate, Nutrition gate, AI limits, etc).
 *
 * DEV OVERRIDE: when localStorage `enferno_view_as_egnite` is `"1"`, the
 * hook returns free-tier state regardless of what the backend says. Lets
 * admins / users-in-trial preview the free-tier UX without changing role,
 * tier, or trial timestamps. Client-side only — backend trust is untouched.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIEW_AS_EGNITE_KEY = 'enferno_view_as_egnite';

const isViewingAsEgnite = () => {
  try { return localStorage.getItem(VIEW_AS_EGNITE_KEY) === '1'; } catch { return false; }
};

const FREE_TIER_STATE = {
  is_paid: false,
  effective_tier: 'egnite',
  reason: 'free',
  trial_end: null,
  trial_days_left: null,
  subscription_tier: 'egnite',
  subscription_status: null,
  plan_id: null,
  current_period_end: null,
  cancel_at_period_end: false,
};

let cached = null;
let inflight = null;

export const useTierState = () => {
  const [state, setState] = useState(cached);
  const [loading, setLoading] = useState(!cached);
  const [overrideOn, setOverrideOn] = useState(isViewingAsEgnite());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (!inflight) {
        inflight = axios.get(`${API}/payments/me`).finally(() => { inflight = null; });
      }
      const res = await inflight;
      cached = res.data;
      setState(res.data);
    } catch {
      // Not signed in / offline → treat as free tier (defensive default)
      cached = { ...FREE_TIER_STATE };
      setState(cached);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cached) refresh();
  }, [refresh]);

  // Listen for the dev override toggle so consumers re-render instantly.
  // Direct state-set in effect is intentional here: we're mirroring an
  // external mutable source (localStorage + custom window event), which is
  // the use case the lint rule's exceptions were designed for. The override
  // is a session-level dev affordance, not part of the React tree.
  useEffect(() => {
    const sync = () => setOverrideOn(isViewingAsEgnite());
    window.addEventListener('enferno:view-as-egnite-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('enferno:view-as-egnite-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (overrideOn) {
    return { ...FREE_TIER_STATE, loading: false, refresh, _override: true };
  }

  return { ...(state || FREE_TIER_STATE), loading, refresh };
};

// Allows external code (e.g., after successful checkout return) to invalidate
// the cache so the next read reflects the new tier state without a page reload.
export const invalidateTierState = () => {
  cached = null;
  inflight = null;
};

// Dev override controls — used by DeveloperSettings panel.
export const isViewAsEgnite = () => isViewingAsEgnite();
export const setViewAsEgnite = (on) => {
  try {
    if (on) localStorage.setItem(VIEW_AS_EGNITE_KEY, '1');
    else localStorage.removeItem(VIEW_AS_EGNITE_KEY);
  } catch { /* ignore */ }
  window.dispatchEvent(new Event('enferno:view-as-egnite-changed'));
};
